import React from 'react';
import clsx from 'clsx';
import {
	Checkbox as MuiCheckbox,
	type CheckboxProps as MuiCheckboxProps,
	FormControlLabel,
} from '@mui/material';
import {useConstCallback} from '@netx/core-components/hooks/useEvent';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {ErrorField} from '@netx/core-components/ErrorField';
import {
	checkboxClasses,
	getCheckboxUtilityClass,
	CHECKBOX_CLASS_NAME,
	type CheckboxClasses,
} from './checkboxClasses';

type CheckboxOwnerState = WithClasses<CheckboxClasses, {}>;

const useUtilityClasses = (ownerState: CheckboxOwnerState) => {
	const {classes} = ownerState;
	const slots = {
		root: ['root'],
	};
	return composeClasses(slots, getCheckboxUtilityClass, classes);
};

// TODO: this should extend the checkbox props
// and have slots for FormControlLabel
export interface CheckboxProps
	extends Omit<MuiCheckboxProps, 'classes' | 'ref'>,
		WithClasses<CheckboxClasses> {
	error?: string;
	hideErrorMessage?: boolean;
	label: string | React.ReactNode;
	ref?: React.ForwardedRef<HTMLDivElement>;
}

const CheckboxRoot = styled('div', {
	name: CHECKBOX_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	display: 'inline-flex',
	flexDirection: 'column',
}));

export const CheckboxPrimitive = (
	{
		classes: _classes,
		className,
		checked,
		disabled,
		error,
		ref: _ref,
		hideErrorMessage,
		label,
		onChange,
		...props
	}: CheckboxProps,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	checked = checked === true;

	const ownerState: CheckboxOwnerState = {
		classes: _classes,
		className,
	};

	const classes = useUtilityClasses(ownerState);

	const handleChange = useConstCallback(
		(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
			// Sometimes a change event will come through that was not related to
			// interacting directly with the input
			// I cannot seem to recreate this in the tests yet, but this is an
			// important chack
			if (!event) return;
			onChange?.(event, checked);
		},
	);

	return (
		<CheckboxRoot ref={ref} className={clsx(classes.root, className)}>
			<FormControlLabel
				disabled={disabled}
				control={
					<MuiCheckbox {...props} checked={checked} onChange={handleChange} />
				}
				label={label}
			/>
			{!hideErrorMessage ? (
				<ErrorField disabled={disabled} error={error} />
			) : null}
		</CheckboxRoot>
	);
};

export const Checkbox = React.forwardRef(CheckboxPrimitive) as (
	props: CheckboxProps,
) => JSX.Element;

export default Checkbox;
